import type { AppearanceModule } from '@setplex/tria-api'
import type { Route } from '!/router'
import { Slot } from '!/router'
import { keys } from '~/shared/jail'
import { Footer } from '~/shared/ui/components/Footer'
import { NoContentBlock } from '~/shared/ui/components/NoContentBlock'
import { AccountSettingsModal } from '~/features/portal/redirect'
import * as pinLockFeature from '~/features/pin-lock'
import { NavBar } from '~/widgets/NavBar'
import {
  AuthorizePaymentForm,
  ExternalPaymentOpen,
  GravyPaymentForm,
  NoPaymentForm,
} from '~/widgets/Payment'
import {
  AnnouncementPopup,
  ProfilesCreatePopup,
  ProfilesRemovePopup,
} from '~/widgets/Popups'
import { SignInToWatchPopup } from '~/widgets/SignInToWatch'
import { PolicyMessage } from '~/features/policy-message'

import * as catchup from './catchup'
import * as dynamic from './dynamic'
import * as epg from './epg'
import * as event from './event'
import * as home from './home'
import * as library from './library'
import * as live from './live'
import * as movie from './movie'
import * as partner from './partner'
import * as search from './search'
import * as series from './series'
import * as settings from './settings'

const standardModule = {
  home: home.route,
  tv_channels: live.route,
  movie: movie.route,
  tv_shows: series.route,
  epg: epg.route,
  catchups: catchup.route,
  events: event.route,
}

const appearanceRoutes: Route[] = []

/**
 * Dynamically update routes based on available modules
 */
export function updateRoutes(modules: AppearanceModule[] | null) {
  // reset routes
  appearanceRoutes.length = 0

  // not ready yet, no routes to add
  if (!modules) return

  let shouldAddDynamic = false

  for (const mod of modules) {
    if (mod.identifier in standardModule && mod.type === 'static') {
      appearanceRoutes.push({
        path: `/${mod.identifier}`,
        load: standardModule[mod.identifier as keyof typeof standardModule],
        children: [], // this is REQUIRED for universal-router to work properly, see `impregnate` function description
      })
    } else if (mod.type === 'dynamic') {
      shouldAddDynamic = true
    }
  }

  if (shouldAddDynamic) {
    appearanceRoutes.push({
      path: `/:identifier`,
      load: dynamic.route,
      children: [], // this is REQUIRED for universal-router to work properly, see `impregnate` function description
    })
  }
}

export const route: Route = {
  keys: [keys.app, keys.guest],

  component: [
    NavBar,
    Slot,
    SignInToWatchPopup,
    pinLockFeature.PinLockPopup,
    AuthorizePaymentForm,
    ExternalPaymentOpen,
    GravyPaymentForm,
    NoPaymentForm,
    NoContentBlock,
    ProfilesRemovePopup,
    ProfilesCreatePopup,
    AnnouncementPopup,
    AccountSettingsModal,
    Footer,
    PolicyMessage,
  ],

  children: [
    // standard subscriber-only modules (unavailable for guest)
    {
      keys: [keys.app],
      path: '/library',
      load: library.route,
    },
    {
      keys: [keys.app],
      path: '/settings',
      load: settings.route,
    },

    // search is available for guest
    {
      path: '/search',
      load: search.route,
    },
    {
      path: '/partner',
      load: partner.route,
    },

    // dynamically managed modules
    {
      children: appearanceRoutes,
    },
  ],
}
